import React from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import type { SimpleAnyObject } from '@clearverify/types';

import { Button } from 'src/components/common';
import { DropzoneFilesField, Field, Form } from 'src/components/finalForm';
import {
  BulkUploadFormFields,
  BulkUploadFormValues,
  ConvertExcelDataWithSchemaFn,
} from 'src/types/bulkUpload';
import { createFilesValidation, parseExcelEntitiesData } from 'src/utils/bulkUpload';
import { validateWithYupSchema } from 'src/utils/yup';

import { ACCEPTED_FILE_TYPES, bulkUploadFormValidationSchema } from './constants';

type EmployersUploadFormProps<TExcelRow, TResultRow> = {
  convertDataWithSchema: ConvertExcelDataWithSchemaFn<TExcelRow, TResultRow>;
  templateSchema: SimpleAnyObject;
  templateDownloadLink: string;
  templateDownloadName?: string;
  topNotice?: React.ReactNode;
  handleEntitiesData: (employers: TResultRow[]) => Promise<void> | void;
  handleCancel: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
  nextStepLoading?: boolean;
};

export const BulkUploadForm = <TExcelRow, TResultRow>({
  convertDataWithSchema,
  templateSchema,
  templateDownloadLink,
  templateDownloadName,
  topNotice,
  handleEntitiesData,
  handleCancel,
  switchClosability,
  nextStepLoading = false,
}: EmployersUploadFormProps<TExcelRow, TResultRow>): React.ReactElement => {
  const handleSubmit = React.useCallback(
    async (formValues: BulkUploadFormValues) => {
      try {
        switchClosability(false);
        const file = formValues.items[0]?.file;
        if (!file) return;

        const employers = await parseExcelEntitiesData(file, convertDataWithSchema);

        handleEntitiesData(employers);
      } finally {
        switchClosability(true);
      }
    },
    [convertDataWithSchema, handleEntitiesData, switchClosability],
  );

  const filesValidation = React.useCallback(() => {
    return createFilesValidation(templateSchema);
  }, [templateSchema]);

  return (
    <Box>
      {topNotice && (
        <Typography variant="body1" color="text.secondary">
          {topNotice}
        </Typography>
      )}

      <Form<BulkUploadFormValues>
        onSubmit={handleSubmit}
        subscription={{ submitting: true, valid: true }}
        validate={validateWithYupSchema(bulkUploadFormValidationSchema)}
      >
        {({ submitting, valid }) => (
          <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
              <Typography variant="subtitle1">Upload File</Typography>

              <Button
                href={templateDownloadLink}
                download={templateDownloadName}
                sx={{ textTransform: 'none' }}
                startIcon={<DownloadIcon />}
              >
                Template
              </Button>
            </Box>

            <Box>
              <Field
                name={BulkUploadFormFields.items}
                component={DropzoneFilesField}
                maxFiles={1}
                disabled={submitting}
                immediateUpload={false}
                validation={filesValidation()}
                acceptFileTypes={ACCEPTED_FILE_TYPES}
              />
            </Box>

            <Box display="flex" justifyContent="center" mt={4} gap={4}>
              <Button onClick={handleCancel} color="error" variant="outlined">
                Cancel
              </Button>

              <Button
                type="submit"
                loading={submitting || nextStepLoading}
                disabled={!valid || submitting}
                variant="contained"
                color="primary"
              >
                Next
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </Box>
  );
};
